import { FormValidator, type FormValidatorConfig, type FormValidatorSettings, isEmpty } from '@common';
import { OfferType } from '@shared';
import { useLocalStorage } from '@vueuse/core';
import { createPipe } from 'imask/esm/index';
import type { Ref } from 'vue';
import { ProductUnitType } from '../enums';

export interface ProductGroupAmountValidatorSettings extends FormValidatorSettings {
  unitType?: ProductUnitType;
  arealimit: number;
  step?: number;
  min?: number;
}

export type ProductGroupAmountValidatorConfig = FormValidatorConfig<ProductGroupAmountValidatorSettings>;

export class ProductGroupAmountValidator extends FormValidator<ProductGroupAmountValidatorSettings> {
  constructor() {
    super();
    this._name = 'productGroupAmount';
    this._settings = {
      arealimit: 0,
      invalidMessage: 'form.validator.productGroupAmount.invalidMessage',
      needTranslate: true
    };
  }

  public override async onValidate(value: any) {
    if (isEmpty(value)) {
      this.reset();
      return;
    }

    const OFFER_TYPE = 'offerType';
    const offerTypeStorage: Ref<OfferType | null> = useLocalStorage(OFFER_TYPE, null);

    const maskPipe = createPipe({ mask: /^[1-9]\D*/ });

    const needAreaLimit =
      offerTypeStorage.value && (JSON.parse(offerTypeStorage.value) as OfferType) !== OfferType.EXTRA;

    if (value) {
      value = maskPipe(value.toString());
    }

    if (!this._settings.unitType || this._settings.unitType !== ProductUnitType.AREA) return;

    if (this._settings.step && value % this._settings.step !== 0) {
      this.addMessage('calculator.validation.amountNotDivisibleByStep');
      this.valid = false;
      return;
    }

    if (this._settings.min && value < this._settings.min) {
      this.addMessage('calculator.validation.amountIsLessThanMin');
      this.valid = false;
      return;
    }

    if (!this._settings.arealimit && needAreaLimit) {
      this.addMessage('calculator.validation.missingAreaLimit');
      this.valid = false;
      return;
    }

    if (!value) {
      this.addMessage('validation.required');
      this.valid = false;
      return;
    }

    if (+this._settings.arealimit < +value && needAreaLimit) {
      this.addMessage('calculator.validation.amountIsBiggerThenAreaLimit');
      this.valid = false;
    }
  }
}
